import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { parseCurrencyNumber } from '../../../../Utils/formatCurrency';

import logoImg from '../../../../assets/Logo/main_logo.png';
import kakaoIconImg from '../../../../assets/Icon/project/kakao_icon.png';
import loadingLottie from '../../../../assets/Icon/lottie/Insider-loading.lottie';
import navbarExampleImg from '../../../../assets/Icon/project/navbar/navbar_example.png';
import profileDefaultImg from '../../../../assets/Icon/project/default-profile.png';
import shareHandImg from '../../../../assets/img/share_hand.png';
import keymanImg from '../../../../assets/img/share_modal/keyman_img.png';

interface PreviewGeneralProps {
  title: string;
  projectId: string;
  goalAmount: string;
  currency: 'KRW' | 'USD';
  mediaPreviewUrl: string;
  videoPreviewSrc?: string; // Add this
  storyHtml: string;
  userFullName: string;
  onClose: () => void;
  onCreate: () => void;
  creating: boolean;
  creatingLabel: string;
}

const DonateIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 32 32" fill="none">
    <path
      d="M23.9993 28C23.9993 25.1711 22.8755 22.458 20.8752 20.4576C18.8748 18.4572 16.1617 17.3334 13.3327 17.3334C10.5037 17.3334 7.7906 18.4572 5.79021 20.4576C3.78982 22.458 2.66602 25.1711 2.66602 28"
      stroke="white"
      strokeWidth="2.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.3327 17.3333C17.0146 17.3333 19.9993 14.3486 19.9993 10.6667C19.9993 6.98477 17.0146 4 13.3327 4C9.65078 4 6.66602 6.98477 6.66602 10.6667C6.66602 14.3486 9.65078 17.3333 13.3327 17.3333Z"
      stroke="white"
      strokeWidth="2.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M29.3337 26.6667C29.3337 22.1733 26.6671 18 24.0004 16C24.8769 15.3424 25.5779 14.4788 26.0411 13.4857C26.5044 12.4926 26.7157 11.4006 26.6564 10.3064C26.5971 9.21219 26.2689 8.14946 25.701 7.21228C25.1331 6.2751 24.3429 5.49237 23.4004 4.93335"
      stroke="white"
      strokeWidth="2.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const KakaoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 3C6.477 3 2 6.916 2 11.75c0 3.168 1.932 5.925 4.865 7.49-.203.746-1.375 4.914-1.422 5.22-.074.482.41.482.604.357.046-.029 4.425-2.962 5.142-3.46.266.038.537.058.811.058 5.523 0 10-3.916 10-8.75S17.523 3 12 3z" />
  </svg>
);

const EnvelopeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M11.666 2.33337H2.33268C1.68835 2.33337 1.16602 2.85571 1.16602 3.50004V10.5C1.16602 11.1444 1.68835 11.6667 2.33268 11.6667H11.666C12.3103 11.6667 12.8327 11.1444 12.8327 10.5V3.50004C12.8327 2.85571 12.3103 2.33337 11.666 2.33337Z" stroke="#5EBDE9" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12.8327 4.08337L7.60018 7.40837C7.42009 7.52121 7.21187 7.58105 6.99935 7.58105C6.78683 7.58105 6.57861 7.52121 6.39852 7.40837L1.16602 4.08337" stroke="#5EBDE9" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PreviewGeneral: React.FC<PreviewGeneralProps> = ({
  title,
  projectId,
  goalAmount,
  currency,
  mediaPreviewUrl,
  storyHtml,
  userFullName,
  onClose,
  onCreate,
  creating,
  creatingLabel,
  videoPreviewSrc,
}) => {
  const { t } = useTranslation('translation');
  const numericGoal = useMemo(() => parseCurrencyNumber(goalAmount), [goalAmount]);
  const projectTitle = title || 'This is a sample for the project';
  
  const today = new Date();
  const dateString = `Created ${today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (KR)`;

  return (
    <PreviewDialog onPointerDown={(e) => e.stopPropagation()}>
      <PreviewHeader>
        <PreviewHeaderTitle>{t('page.newProject.preview.topTitle')}</PreviewHeaderTitle>
        <PreviewCloseButton type="button" onClick={onClose} aria-label="close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="#17171B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="#17171B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </PreviewCloseButton>
      </PreviewHeader>
      <PreviewNavbar src={navbarExampleImg} alt="Navbar" />
      <PreviewBody>
        <GeneralContainer>
            <TopWhiteSection>
                {/* Header / Title Area */}
                <HeaderWrapper>
                    <HeaderArea>
                        <Title>{projectTitle}</Title>
                    <UserProfile>
                        <UserAvatar src={profileDefaultImg} />
                        <UserInfo>
                            <UserName>{userFullName || 'User Name'}</UserName>
                            <UserDateRow>
                                <EnvelopeIcon />
                                <UserDate>{dateString}</UserDate>
                            </UserDateRow>
                        </UserInfo>
                    </UserProfile>
                    </HeaderArea>
                </HeaderWrapper>

                {/* Top Grid: Image + Donation Box */}
                <TopGrid>
                    <MainImageArea>
                        {mediaPreviewUrl ? (
                            <MainImage src={mediaPreviewUrl} />
                        ) : videoPreviewSrc ? (
                            <PreviewIframe
                                src={videoPreviewSrc}
                                title="video preview"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        ) : (
                            <EmptyImage>{t('page.newProject.preview.emptyImage')}</EmptyImage>
                        )}
                    </MainImageArea>
                    
                    <DonationBox>
                        <AmountArea>
                            <CurrentLabel>{t('page.newProject.preview.currentLabel')}</CurrentLabel>
                            <AmountValue>
                                <CurrencySymbol>{currency === 'KRW' ? '₩' : '$'}</CurrencySymbol>
                                0
                                <PerMonth>{t('page.newProject.preview.perMonth')}</PerMonth>
                            </AmountValue>
                        </AmountArea>

                        <ProgressBarTrack>
                            <ProgressBarFill style={{ width: '0%' }} />
                        </ProgressBarTrack>
                        <ProgressStats>
                            <StatText>0%</StatText>
                        </ProgressStats>
                        
                        <RaisedText>
                            Raised by 0 people in 0 days ($1 = ₩1,000)
                        </RaisedText>

                        <ActionButtons>
                            <DonateButton>
                                <BtnIcon><DonateIcon /></BtnIcon> {t('common.actions.donate')}
                            </DonateButton>
                            <ShareButton>
                                <BtnIcon><img src={kakaoIconImg} alt="kakao" width="20" height="20" /></BtnIcon> {t('common.actions.share')}
                            </ShareButton>
                        </ActionButtons>
                        
                        <ProjectLink>
                            go.missionfund.org/{projectId || 'sample'}
                        </ProjectLink>
                        
                        <KeymanText>
                            <KeymanImage src={keymanImg} alt={t('page.newProject.preview.keymanBadge')} />
                        </KeymanText>
                    </DonationBox>
                </TopGrid>
            </TopWhiteSection>

            <BottomGrid>
                <StorySection>
                    <SectionLabel>TOTAL SHARES</SectionLabel>
                    <StoryCard>
                        <StoryContent>
                             {storyHtml ? (
                                <div dangerouslySetInnerHTML={{ __html: storyHtml }} />
                              ) : (
                                <EmptyStory>{t('page.newProject.preview.emptyStory')}</EmptyStory>
                              )}
                        </StoryContent>
                    </StoryCard>
                </StorySection>

                <RightSidebar>
                    <FirstSponsorBox>
                        <FirstSponsorTitle>{t('page.newProject.preview.firstSponsorTitle')}</FirstSponsorTitle>
                        <EmptyWhiteBox />
                    </FirstSponsorBox>
                </RightSidebar>
            </BottomGrid>
        </GeneralContainer>

        <PreviewFooterArea>
          <FooterLogo src={logoImg} alt="Mission Fund" />
          <FooterMission>
            <FooterMissionText>{t('page.newProject.preview.footerMissionKo')}</FooterMissionText>
            <FooterMissionText $blue>MissionFund helps the Body of Christ fulfill its mission.</FooterMissionText>
          </FooterMission>
          {/* ... footer content same as Partner ... */}
             <FooterButtons>
            <FooterButton>{t('page.newProject.preview.footerButtons.receipt')}</FooterButton>
            <FooterButton>{t('page.newProject.preview.footerButtons.donations')}</FooterButton>
            <FooterButton>{t('page.newProject.preview.footerButtons.story')}</FooterButton>
            <FooterButton>{t('page.newProject.preview.faq')}</FooterButton>
          </FooterButtons>

          <FooterDesc>
            <p>{t('page.newProject.preview.footerInfo.line1')}</p>
            <p>{t('page.newProject.preview.footerInfo.line2')}</p>
            <p>{t('page.newProject.preview.footerInfo.line3')}</p>
          </FooterDesc>

          <FooterContact>
            {t('page.newProject.preview.footerContact.line1')}<br />
            {t('page.newProject.preview.footerContact.line2')}<br />
            {t('page.newProject.preview.footerContact.line3')}
          </FooterContact>
        </PreviewFooterArea>
      </PreviewBody>

      <PreviewFooter>
        <PreviewConfirm
          type="button"
          onClick={onCreate}
          disabled={creating}
          $loading={creating}
          aria-busy={creating}
          aria-label={creating ? creatingLabel : undefined}
        >
          {creating ? (
            <LoadingIcon
              src={loadingLottie}
              autoplay
              loop
              aria-hidden
              style={{ width: 12, height: 12 }}
            />
          ) : t('common.actions.confirm')}
        </PreviewConfirm>
      </PreviewFooter>
    </PreviewDialog>
  );
};

export default PreviewGeneral;

// Styled Components

const PreviewDialog = styled.div`
  width: min(1200px, 95vw);
  height: 90vh;
  background: #f0f1f2;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const PreviewHeader = styled.div`
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background: #ffffff;
  border-bottom: 1px solid #eeeeee;
  flex-shrink: 0;
`;

const PreviewHeaderTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #17171b;
`;

const PreviewCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewNavbar = styled.img`
  width: 100%;
  height: auto;
  display: block;
  flex-shrink: 0;
`;

const PreviewBody = styled.div`
  flex: 1;
  background: #f0f1f2;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
`;

const GeneralContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  background: #F3F4F6;
  padding: 0;
  margin-top: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  border-radius: 8px;
`;

const HeaderArea = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 24px;
    min-width: 0;
`;

const Title = styled.h1`
    color: #171717;
    font-family: "Noto Sans KR";
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%;
    margin: 0;
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
`;

const UserProfile = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: 20px;
    flex: 0 1 auto;
    min-width: 0;
    max-width: min(320px, 42%);
`;

const UserAvatar = styled.img`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #ccc;
`;

const UserInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
    flex: 1;
`;

const UserName = styled.div`
    color: #6B7280;
    font-family: "Noto Sans KR";
    font-size: 14px;
    font-style: normal;
    font-weight: 350;
    line-height: 21px;
    max-width: 100%;
    overflow-wrap: anywhere;
    word-break: break-word;
`;

const UserDateRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    max-width: 100%;
`;

const UserDate = styled.div`
    color: #5EBDE9;
    font-family: "Noto Sans KR";
    font-size: 13px;
    font-style: normal;
    font-weight: 350;
    line-height: 19.5px;
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
`;

const TopWhiteSection = styled.div`
    background: #fff;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
`;

const HeaderWrapper = styled.div`
    padding: 40px 40px 0;
`;

const TopGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 0;
`;



const MainImageArea = styled.div`
    width: 100%;
    height: 100%;
    background: #eee;
    overflow: hidden;
    position: relative;
`;

const PreviewShareHand = styled.img`
  position: absolute;
  bottom: 0;
  right: 50px;
  width: 100px;
  height: auto;
  z-index: 1;
`;

const MainImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const PreviewIframe = styled.iframe`
    width: 100%;
    height: 100%;
    border: none;
`;

const EmptyImage = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #aaa;
`;

const DonationBox = styled.div`
    background: #F1F8E9; // Light green/yellowish bg
    padding: 24px;
    display: flex;
    flex-direction: column;
`;

const AmountArea = styled.div`
    margin-bottom: 12px;
`;

const CurrentLabel = styled.div`
    font-size: 14px;
    color: #666;
`;

const AmountValue = styled.div`
    font-size: 32px;
    font-weight: 700;
    color: #888; // Placeholder color
    display: flex;
    align-items: baseline;
    gap: 4px;
`;

const CurrencySymbol = styled.span`
    font-size: 24px;
    color: #aaa;
`;

const PerMonth = styled.span`
    font-size: 16px;
    color: #aaa;
`;

const ProgressBarTrack = styled.div`
    height: 8px;
    background: #fff;
    border-radius: 4px;
    margin-bottom: 4px;
`;

const ProgressBarFill = styled.div`
    height: 100%;
    background: #88C541; // Green
    border-radius: 4px;
`;

const ProgressStats = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 8px;
`;

const StatText = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: #666;
`;

const RaisedText = styled.div`
    font-size: 12px;
    color: #888;
    margin-bottom: 20px;
`;

const ActionButtons = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 20px;
`;

const DonateButton = styled.button`
    width: 100%;
    height: 48px;
    background: #D32F2F; // Red
    color: #fff;
    border: none;
    border-radius: 4px;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
`;

const ShareButton = styled.button`
    width: 100%;
    height: 48px;
    background: #FDD835; // Yellow
    color: #333;
    border: none;
    border-radius: 4px;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
`;

const BtnIcon = styled.span`
    font-size: 20px;
`;

const ProjectLink = styled.div`
    font-size: 14px;
    color: #666;
    text-align: center;
    margin-bottom: 16px;
    word-break: break-all;
`;

const KeymanText = styled.div`
    display: flex;
    justify-content: center;
    margin-top: -16px;
`;

const KeymanImage = styled.img`
    width: 160px;
    height: auto;
    object-fit: contain;
`;

const InnerPadding = styled.div`
    padding: 40px 40px 0;
    background: #fff;
`;

const BottomGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 30px;
    padding: 20px 16px;
`;

const StoryCard = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 24px;
`;

const StorySection = styled.div`
    display: flex;
    flex-direction: column;
`;

const SectionLabel = styled.div`
    color: #6B7280;
    font-family: "Noto Sans KR";
    font-size: 21px;
    font-style: normal;
    font-weight: 350;
    line-height: 20px;
    margin-bottom: 16px;
    text-transform: uppercase;
`;

const StoryContent = styled.div`
    color: #171717;
    font-family: NanumGothic;
    font-size: 21px;
    font-style: normal;
    font-weight: 400;
    line-height: 37.8px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    word-break: break-word;
    margin-bottom: 30px;

    p,
    div,
    span,
    li,
    ul,
    ol {
        color: #171717;
        font-family: NanumGothic;
        font-size: 21px;
        font-style: normal;
        font-weight: 400;
        line-height: 37.8px;
        overflow-wrap: anywhere;
        word-break: break-word;
    }
    
    img {
        max-width: 100%;
    }
`;

const EmptyStory = styled.div`
    color: #ccc;
    text-align: center;
    padding: 20px;
`;

const ReactionImage = styled.img`
    width: 100%;
    height: auto;
    margin-top: 20px;
`;

const RightSidebar = styled.div`
    padding-top: 0;
`;

const FirstSponsorBox = styled.div`
    
`;

const FirstSponsorTitle = styled.div`
    color: #87CEEB;
    font-family: "Noto Sans KR";
    font-size: 16px;
    font-style: normal;
    font-weight: 350;
    line-height: 24px;
    margin-bottom: 16px;
    margin-top: -2px;
`;

const EmptyWhiteBox = styled.div`
    background: #fff;
    border: 1px solid #eee;
    height: 100px;
    border-radius: 8px;
    margin-top: 2px;
`;


// Shared Footer Styles (Same as Partner)
const PreviewFooterArea = styled.div`
  width: 100%;
  margin-top: 60px;
  background: #f0f1f2;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 24px 120px;
`;

const FooterLogo = styled.img`
  height: 54px;
  margin-bottom: 24px;
`;

const FooterMission = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const FooterMissionText = styled.div<{ $blue?: boolean }>`
  color: #27AAE2;
  text-align: center;
  font-family: "Noto Sans KR";
  font-size: 20px;
  font-style: normal;
  font-weight: 350;
  line-height: 30px;
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  width: 100%;
  max-width: 700px;
`;

const FooterButton = styled.div`
  background: #888;
  color: #fff;
  padding: 12px 24px;
  border-radius: 6px;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-width: fit-content;
  white-space: nowrap;
`;

const FooterDesc = styled.div`
  text-align: left;
  color: #374151;
  font-family: "Noto Sans KR";
  font-size: 16px;
  font-style: normal;
  font-weight: 350;
  line-height: 24px;
  max-width: 700px;
  margin-top: 32px;
  
  p {
    margin-bottom: 16px;
    word-break: keep-all;
  }
`;

const FooterContact = styled.div`
  text-align: center;
  color: #6B7280;
  font-family: "Noto Sans KR";
  font-size: 12px;
  font-style: normal;
  font-weight: 350;
  line-height: 16px;
  max-width: 700px;
  width: 100%;
  border-top: 1px solid #aaa;
  padding-top: 24px;
  margin-top: 24px;
`;

const PreviewFooter = styled.div`
  height: 76px;
  padding: 0 24px;
  background: #ffffff;
  border-top: 1px solid #eeeeee;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  z-index: 10;
`;

const PreviewConfirm = styled.button<{ $loading?: boolean }>`
  width: 130px;
  height: 54px;
  padding: 10px;
  border-radius: 4px;
  border: none;
  background: ${({ $loading }) => ($loading ? '#EBFCFF' : '#026478')};
  color: ${({ $loading }) => ($loading ? '#2BC2E1' : '#ffffff')};
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%;
  cursor: pointer;
  transition: background 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  white-space: nowrap;

  &:hover {
    background: ${({ $loading }) => ($loading ? '#EBFCFF' : '#09525d')};
  }

  &:disabled {
    background: ${({ $loading }) => ($loading ? '#EBFCFF' : '#bcbcbc')};
    color: ${({ $loading }) => ($loading ? '#2BC2E1' : '#ffffff')};
    cursor: not-allowed;
  }
`;

const LoadingIcon = styled(DotLottieReact)`
  display: inline-flex;
`;
